import {useIntl} from 'react-intl'
import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {KTSVG} from '../../../../template/helpers'
import {appsAPI} from '../../../../services/AppsService'
import {usersAPI} from '../../../../services/UsersService'
import {IAppsModel} from '../../../../types/apps/AppsModel'

// Setting is visible and editable only for service admins (docs/PERSONAL_DATA.md)
const PersonalData = () => {
  const intl = useIntl()
  const {appId} = useParams()
  const {data: currentUser} = usersAPI.useFetchGetMeQuery()
  const {data: app} = appsAPI.useFetchAppByIdQuery(appId!)
  const [updateApp, {isLoading}] = appsAPI.useUpdateAppMutation()
  const [editState, updateEditState] = useState(false)
  const [personalData, updatePersonalData] = useState(false)
  const [cdrClientNames, updateCdrClientNames] = useState(false)
  // client names in CDR are available only for Bitrix24 apps with active call log (docs/PERSONAL_DATA.md)
  const cdrClientNamesAvailable = app?.crm === 'BITRIX24' && app?.extensions?.cdr === true

  const resetState = () => {
    if (app) {
      updatePersonalData(app.personal_data)
      updateCdrClientNames(app.cdr_client_names)
    }
  }

  useEffect(() => {
    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app])

  if (currentUser?.role !== 'ADMIN') {
    return null
  }

  const handleSave = async () => {
    // client names in CDR make sense only with personal data, so they are switched off together
    await updateApp({
      id: appId,
      personal_data: personalData,
      cdr_client_names: personalData && cdrClientNamesAvailable && cdrClientNames,
    } as IAppsModel)
    updateEditState(false)
  }

  const handleCancel = () => {
    resetState()
    updateEditState(false)
  }

  return (
    <div className='card card-custom card-stretch shadow-sm mb-5'>
      <div className='card-header'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>{intl.formatMessage({id: 'APPS.SETTINGS.PERSONAL_DATA'})}</h3>
        </div>
        {!editState && (
          <button className='btn btn-sm btn-primary align-self-center' onClick={() => updateEditState(true)}>
            <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-info me-1 align-self-center' />
            {intl.formatMessage({id: 'BUTTON.EDIT'})}
          </button>
        )}
      </div>
      <div className='card-body pt-5 pb-5'>
        <div className='row d-flex align-items-center'>
          <label className='col-lg-4 fw-bolder text-gray-600 text-uppercase small'>
            {intl.formatMessage({id: 'APPS.SETTINGS.PERSONAL_DATA'})}
          </label>
          <div className='col-lg-8 d-flex align-items-center'>
            {!editState && app?.personal_data === true && <span className='badge badge-light-success'>ON</span>}
            {!editState && app?.personal_data === false && <span className='badge badge-light-danger'>OFF</span>}
            {editState && (
              <div className='form-check form-switch form-check-custom form-check-solid me-10'>
                <input
                  className='form-check-input h-20px w-30px align-self-center'
                  type='checkbox'
                  checked={personalData}
                  onChange={() => updatePersonalData(!personalData)}
                />
              </div>
            )}
          </div>
        </div>
        <div className='text-muted small mt-3'>
          {intl.formatMessage({id: 'APPS.SETTINGS.PERSONAL_DATA.DESCRIPTION'})}
        </div>
        {cdrClientNamesAvailable && ((!editState && app?.personal_data === true) || (editState && personalData)) && (
          <>
            <div className='row d-flex align-items-center mt-5'>
              <label className='col-lg-4 fw-bolder text-gray-600 text-uppercase small'>
                {intl.formatMessage({id: 'APPS.SETTINGS.PERSONAL_DATA.CDR_CLIENT_NAMES'})}
              </label>
              <div className='col-lg-8 d-flex align-items-center'>
                {!editState && app?.cdr_client_names === true && <span className='badge badge-light-success'>ON</span>}
                {!editState && app?.cdr_client_names === false && <span className='badge badge-light-danger'>OFF</span>}
                {editState && (
                  <div className='form-check form-switch form-check-custom form-check-solid me-10'>
                    <input
                      className='form-check-input h-20px w-30px align-self-center'
                      type='checkbox'
                      checked={cdrClientNames}
                      onChange={() => updateCdrClientNames(!cdrClientNames)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className='text-muted small mt-3'>
              {intl.formatMessage({id: 'APPS.SETTINGS.PERSONAL_DATA.CDR_CLIENT_NAMES.DESCRIPTION'})}
            </div>
          </>
        )}
        {editState && (
          <div className='card-footer d-flex justify-content-end py-2 px-1' style={{border: 'none'}}>
            <button className='btn btn-sm btn-primary me-3' disabled={isLoading} onClick={handleSave}>
              {!isLoading && intl.formatMessage({id: 'BUTTON.SAVE'})}
              {isLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  {intl.formatMessage({id: 'WAIT'})}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
            <button className='btn btn-sm btn-light-primary align-self-center' onClick={handleCancel}>
              {intl.formatMessage({id: 'BUTTON.CANCEL'})}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export {PersonalData}
